/**
 * User preferences: UI language, target country for jobs, target country
 * for services, optional default location query.
 *
 * Persisted in localStorage under a single versioned key so we can evolve
 * the shape safely without stale client crashes.
 *
 *   localStorage["keikka_prefs_v1"] = JSON.stringify({
 *     uiLang:          "et" | "en" | "fi" | "ru" | ...,
 *     workCountry:     "EE" | "FI" | "NO" | "" | ...,   // where I want to work
 *     serviceCountry:  "EE" | "FI" | "NO" | "" | ...,   // where I request/offer services
 *     locationQuery:   ""                                // optional city / area default
 *   })
 *
 * Consumers: GlobalNav (Preferences dialog), MagicBar (homepage search
 * defaults), Jobs pages (default country filter), Map page (default
 * country + search text).
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const LS_KEY = 'keikka_prefs_v1';

const DEFAULTS = {
  uiLang: '',
  workCountry: '',
  serviceCountry: '',
  locationQuery: '',
};

function loadFromStorage() {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(LS_KEY) : null;
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return { ...DEFAULTS, ...parsed };
    }
  } catch (_) {
    // corrupt JSON — fall through to defaults
  }
  return { ...DEFAULTS };
}

function saveToStorage(prefs) {
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(prefs));
  } catch (_) {
    // quota / private mode — silently ignore
  }
}

const PrefsContext = createContext(null);

export const PrefsProvider = ({ children }) => {
  const [prefs, setPrefs] = useState(() => loadFromStorage());

  // Keep localStorage in sync whenever prefs change
  useEffect(() => {
    saveToStorage(prefs);
  }, [prefs]);

  // React to changes from other tabs / windows
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === LS_KEY) {
        setPrefs(loadFromStorage());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const updatePrefs = useCallback((patch) => {
    setPrefs((prev) => ({ ...prev, ...patch }));
  }, []);

  const resetPrefs = useCallback(() => {
    setPrefs({ ...DEFAULTS });
  }, []);

  const value = useMemo(
    () => ({ prefs, updatePrefs, resetPrefs, PREFS_STORAGE_KEY: LS_KEY }),
    [prefs, updatePrefs, resetPrefs],
  );

  return <PrefsContext.Provider value={value}>{children}</PrefsContext.Provider>;
};

export const usePrefs = () => {
  const ctx = useContext(PrefsContext);
  if (!ctx) {
    // Fallback that still works if a component is rendered outside the provider
    // (e.g. in a test) — reads localStorage directly, writes are no-op.
    return {
      prefs: loadFromStorage(),
      updatePrefs: () => {},
      resetPrefs: () => {},
      PREFS_STORAGE_KEY: LS_KEY,
    };
  }
  return ctx;
};

export const PREFS_STORAGE_KEY = LS_KEY;
